import { ClinicDayHours, DAYS_OF_WEEK, DayOfWeek } from "@/types/clinic-types";
import { formatters } from "@/utils/fhir-utils";
import { DEFAULT_CLINIC_TIMEZONE } from "@/utils/timezones";

import { fhirR4 } from "@smile-cdr/fhirts";
import dayjs, { Dayjs } from "dayjs";

// Extension URLs
export const PATIENT_CLINIC_EXTENSION_URL = "http://vitall.com/fhir/ext/patient-clinic";
export const PRIMARY_CONTACT_EXTENSION_URL = "http://vitall.com/fhir/ext/primary-contact-practitioner";
export const CLINIC_TIMEZONE_EXTENSION_URL = "https://vitall.com/fhir/ext/clinic-timezone";
// Links the Organization to its contained Location (which holds hours + timezone).
export const CLINIC_LOCATION_EXTENSION_URL = "https://vitall.com/fhir/ext/clinic-location";

// id of the contained Location resource on the clinic Organization.
export const CONTAINED_LOCATION_ID = "clinic-location";

// Default operating hours: Mon–Fri 08:00–16:00 open, weekends closed.
const DEFAULT_OPEN_START = "08:00";
const DEFAULT_OPEN_END = "16:00";
const WEEKDAYS: DayOfWeek[] = ["mon", "tue", "wed", "thu", "fri"];

/**
 * Default clinic hours used for new clinics and as the fallback when an
 * existing clinic has no stored hours (backfill): Mon–Fri 08:00–16:00 open,
 * Saturday and Sunday closed.
 */
export const getDefaultClinicHours = (): ClinicDayHours[] => {
  return DAYS_OF_WEEK.map(day => ({
    day,
    open: WEEKDAYS.includes(day),
    start: DEFAULT_OPEN_START,
    end: DEFAULT_OPEN_END,
  }));
};

// --- Clinic-hours time conversions -----------------------------------------
// The form represents a time as a 24h "HH:mm" string. These helpers convert
// between that, the FHIR `time` ("HH:mm:ss") stored on the resource, and the
// Dayjs value the MUI TimePicker works with.

/** Converts a "HH:mm" form value to a FHIR `time` ("HH:mm:ss"). */
export const toFhirTime = (value: string): string => {
  return /^\d{2}:\d{2}$/.test(value) ? `${value}:00` : value;
};

/** Converts a FHIR `time` ("HH:mm:ss") back to a "HH:mm" form value. */
export const fromFhirTime = (value: string | undefined): string => {
  if (!value) {
    return DEFAULT_OPEN_START;
  }
  const match = value.match(/^(\d{2}:\d{2})/);
  return match ? match[1] : value;
};

/** Parses a "HH:mm" form value into a Dayjs (on a fixed date) for the TimePicker. */
export const toDayjsTime = (value: string): Dayjs | null => {
  const parsed = dayjs(`2000-01-01 ${value}`);
  return parsed.isValid() ? parsed : null;
};

/** Formats a Dayjs from the TimePicker back to a "HH:mm" form value. */
export const fromDayjsTime = (value: Dayjs | null): string => {
  return value && value.isValid() ? value.format("HH:mm") : "";
};

/**
 * Builds the clinic-timezone extension (stored on the contained Location).
 */
export const buildClinicTimezoneExtension = (timezone: string): fhirR4.Extension => {
  return {
    url: CLINIC_TIMEZONE_EXTENSION_URL,
    valueString: timezone,
  };
};

/**
 * Converts the per-day form hours into FHIR `Location.hoursOfOperation` —
 * one entry per open day. Closed days are simply omitted (FHIR convention).
 */
export const buildClinicHoursOfOperation = (hours: ClinicDayHours[]): fhirR4.LocationHoursOfOperation[] => {
  return hours
    .filter(dayHours => dayHours.open)
    .map(dayHours => ({
      daysOfWeek: [dayHours.day],
      openingTime: toFhirTime(dayHours.start),
      closingTime: toFhirTime(dayHours.end),
    }));
};

/**
 * Builds the contained Location resource holding the clinic's operating hours
 * (`hoursOfOperation`) and timezone (extension). Referenced from the
 * Organization via the clinic-location extension.
 */
export const buildClinicLocation = (timezone: string, hours: ClinicDayHours[]): fhirR4.Location => {
  return {
    resourceType: "Location",
    id: CONTAINED_LOCATION_ID,
    status: fhirR4.Location.StatusEnum.Active,
    extension: [buildClinicTimezoneExtension(timezone)],
    hoursOfOperation: buildClinicHoursOfOperation(hours),
  };
};

/**
 * Builds the Organization extension that references the contained Location.
 */
export const buildClinicLocationExtension = (): fhirR4.Extension => {
  return {
    url: CLINIC_LOCATION_EXTENSION_URL,
    valueReference: { reference: `#${CONTAINED_LOCATION_ID}` },
  };
};

/**
 * Returns the clinic's contained Location resource, if present.
 */
export const getClinicLocation = (clinic: fhirR4.Organization): fhirR4.Location | undefined => {
  return clinic.contained?.find((resource): resource is fhirR4.Location => resource.resourceType === "Location");
};

/**
 * Reads the clinic timezone from its contained Location, falling back to the
 * default when not set.
 */
export const getClinicTimezone = (clinic: fhirR4.Organization): string => {
  const ext = getClinicLocation(clinic)?.extension?.find(e => e.url === CLINIC_TIMEZONE_EXTENSION_URL);
  return ext?.valueString || DEFAULT_CLINIC_TIMEZONE;
};

/**
 * Reads the clinic operating hours from its contained Location's
 * `hoursOfOperation`. Falls back to the default schedule (Mon–Fri 08:00–16:00)
 * when the clinic has no Location. Always returns seven entries ordered
 * Monday → Sunday; days absent from `hoursOfOperation` are treated as closed.
 */
export const getClinicHours = (clinic: fhirR4.Organization): ClinicDayHours[] => {
  const location = getClinicLocation(clinic);
  if (!location) {
    return getDefaultClinicHours();
  }

  // Start with every day closed, then open the days present in hoursOfOperation.
  const byDay = new Map<DayOfWeek, ClinicDayHours>(
    DAYS_OF_WEEK.map(day => [day, { day, open: false, start: DEFAULT_OPEN_START, end: DEFAULT_OPEN_END }])
  );

  for (const entry of location.hoursOfOperation ?? []) {
    for (const code of entry.daysOfWeek ?? []) {
      const day = code as DayOfWeek;
      if (DAYS_OF_WEEK.includes(day)) {
        byDay.set(day, {
          day,
          open: true,
          start: fromFhirTime(entry.openingTime),
          end: fromFhirTime(entry.closingTime),
        });
      }
    }
  }

  return DAYS_OF_WEEK.map(day => byDay.get(day)!);
};

/**
 * Creates a new clinic Organization resource. Operating hours and timezone are
 * stored on a contained Location (referenced via the clinic-location extension).
 */
export const createClinicOrganization = (
  name: string,
  phoneNumber: string,
  parentOrganizationId: string,
  primaryContactRef: fhirR4.Reference | undefined,
  timezone: string,
  hours: ClinicDayHours[]
): fhirR4.Organization => {
  const extension: fhirR4.Extension[] = [];
  if (primaryContactRef) {
    extension.push({ url: PRIMARY_CONTACT_EXTENSION_URL, valueReference: primaryContactRef });
  }
  extension.push(buildClinicLocationExtension());

  const clinic: fhirR4.Organization = {
    resourceType: "Organization",
    active: true,
    name,
    telecom: [
      {
        system: fhirR4.ContactPoint.SystemEnum.Phone,
        value: phoneNumber,
        use: fhirR4.ContactPoint.UseEnum.Work,
        rank: 1,
      },
    ],
    partOf: {
      reference: formatters.formatOrganization(parentOrganizationId),
    },
    contained: [buildClinicLocation(timezone, hours)],
    extension,
  };

  return clinic;
};

/**
 * Gets the primary contact practitioner from a clinic
 */
export const getClinicPrimaryContact = (clinic: fhirR4.Organization): fhirR4.Reference | undefined => {
  return clinic.extension?.find(ext => ext.url === PRIMARY_CONTACT_EXTENSION_URL)?.valueReference;
};

/**
 * Extracts clinic information from Organization resource
 */
export const extractClinicInfo = (clinic: fhirR4.Organization) => {
  const phoneNumber = clinic.telecom?.find(t => t.system === "phone")?.value || "";
  const primaryContact = getClinicPrimaryContact(clinic);

  return {
    id: clinic.id,
    name: clinic.name || "",
    phoneNumber,
    active: clinic.active !== false,
    primaryContact,
    parentOrganization: clinic.partOf?.reference,
    timezone: getClinicTimezone(clinic),
    hours: getClinicHours(clinic),
  };
};
